/**
 * @file Shared HTTP route contracts + identity-domain value objects
 *       for the admin daemon's `/api/account/*` + `/api/learners` surface.
 * @module @labcraft/admin/shared
 *
 * The admin daemon owns one auth flow: Google Workspace, gated by the
 * `hd=labcraft.dev` claim. Sign-in / callback / me / signout live
 * under `/api/account/*`; the cookie name is `lc_admin_session` and is
 * **host-only** (no `Domain=` attribute on `Set-Cookie`) so it never
 * leaks across the `*.labcraft.dev` subdomains where the learner
 * cookie lives.
 *
 * Per umbrella #179: schema-level wall between learners and admins —
 * distinct tables (`admins` / `admin_identities` vs `learners` /
 * `learner_identities`), distinct cookies (one host-only, one
 * domain-scoped), distinct routes, no shared discriminator.
 *
 * `IdentityClaims` is duplicated here (rather than imported from
 * `@labcraft/account`) per the same wall: admin's OIDC plumbing is
 * fully owned by this package.
 *
 * The browser-driven OAuth round-trip (GET begin → 302 to Google →
 * GET callback → 302 to '/') uses `z.void()` for the response schema
 * — the JSON body is empty on the 302 path.
 */

import { z } from 'zod'
import { CoreAnalytics, CoreData, defineRoute } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// Identity claims (verified per-flow facts from the Workspace provider)
////////////////////////////////////////////////////////////////////////////////

/**
 * Schema for verified identity claims. `GoogleWorkspaceProvider`
 * guarantees `emailVerified === true` and a matching `hd` before
 * returning claims; consumers downstream of the provider don't need
 * to re-verify.
 *
 * @property provider - Provider id (`'google-workspace'` today).
 * @property sub - Stable per-provider subject id.
 * @property email - Lower-cased verified email.
 * @property emailVerified - Always `true` when claims are returned.
 * @property hd - Workspace hosted-domain claim. Always set on
 *   admin claims; the provider rejects callbacks without it.
 * @property name - Optional human-readable name.
 * @property avatarUrl - Provider-hosted profile-picture URL.
 */
export const IdentityClaimsSchema = z.object({
  provider: z.string().min(1),
  sub: z.string().min(1),
  email: z.string().email(),
  emailVerified: z.literal(true),
  hd: z.string().optional(),
  name: z.string().optional(),
  avatarUrl: z.string().url().optional(),
})

/**
 * Verified identity claims returned by `callback` on every provider.
 * Caller-side policy (admin row upsert) runs against this shape.
 */
export class IdentityClaims extends CoreData<typeof IdentityClaimsSchema> {
  static override readonly schema = IdentityClaimsSchema

  declare readonly provider: string
  declare readonly sub: string
  declare readonly email: string
  declare readonly emailVerified: true
  declare readonly hd?: string
  declare readonly name?: string
  declare readonly avatarUrl?: string
}

////////////////////////////////////////////////////////////////////////////////
// Auth service props
////////////////////////////////////////////////////////////////////////////////

/**
 * Schema for the OIDC client config consumed by the admin's
 * `GoogleWorkspaceProvider`. All fields required — defaults live in
 * the daemon's `Daemon.run({ defaults })` per
 * `.agents/rules/design.md` § "Defaults belong to the operator".
 *
 * @property issuerUrl - OIDC issuer URL. Production:
 *   `https://accounts.google.com`. Tests override to the in-process
 *   `FakeIdp` (OIDC mode).
 * @property clientId - Workspace OAuth 2.0 client ID. Supplied via
 *   `LC_ADMIN_OIDC_CLIENT_ID` at deploy time.
 * @property hostedDomain - The `hd` (hosted-domain) claim value a
 *   callback ID token must carry. Production: `'labcraft.dev'`.
 *   Supplied via `LC_ADMIN_OIDC_HOSTED_DOMAIN`. Required (no
 *   default) — a missing hosted-domain lock would turn the admin
 *   surface into open consumer auth.
 *
 * `clientSecret` is NOT a config field — the daemon reads it from
 * `kv/admin/oidc/client_secret` in Bao at `onStart` (matching
 * `@labcraft/account`'s `providers/<name>/client_secret` shape), so
 * it stays operator-rotatable via the admin Secrets sub-panel and
 * never touches the nix store or the daemon's env vars.
 */
export const OidcPropsSchema = z.object({
  issuerUrl: z.string().url(),
  clientId: z.string().min(1),
  hostedDomain: z.string().min(1),
})

/** Input type (pre-parse) for `OidcPropsSchema`. */
export type OidcProps = z.input<typeof OidcPropsSchema>

/**
 * Schema for the admin-side `Auth` service. All fields required —
 * defaults live in the daemon's `Daemon.run({ defaults })` per
 * `.agents/rules/design.md` § "Defaults belong to the operator".
 *
 * The daemon's browser-facing `publicUrl` is a SEPARATE top-level
 * field on `AdminConfigSchema` rather than living here — it's a
 * daemon-wide concept (one host, one public URL).
 *
 * @property sessionTtl - Sliding session lifetime, ms. Admin
 *   sessions default shorter than learner sessions — admin privilege
 *   is higher-risk so a lapsed login is the safer default.
 * @property flowTtl - OAuth flow-row + flow-cookie lifetime, ms.
 *   Bounds how long an admin has between clicking "Sign in" and
 *   Google handing them back to the callback URL.
 */
export const AuthPropsSchema = z.object({
  sessionTtl: z.number().int().positive(),
  flowTtl: z.number().int().positive(),
})

/** Input type (pre-parse) for `AuthPropsSchema`. */
export type AuthProps = z.input<typeof AuthPropsSchema>

/**
 * Runtime constructor input for `Auth`. Combines the operator-tunable
 * `AuthProps` with the daemon-wide `publicUrl` (composed in by
 * `Admin`).
 *
 * Defined as the schema-extension (not a bare `&` type) so `Auth`'s
 * constructor can `parse()` the full shape and surface a `ZodError`
 * on a malformed `publicUrl` rather than silently trusting whatever
 * `Admin` passed.
 */
export const AuthRuntimePropsSchema = AuthPropsSchema.extend({
  publicUrl: z.string().url(),
})
export type AuthRuntimeProps = z.input<typeof AuthRuntimePropsSchema>

////////////////////////////////////////////////////////////////////////////////
// /api/account/google-workspace/{authorize,callback} (browser-driven OAuth)
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /api/account/google-workspace/authorize?next=…` — start the
 * Workspace OAuth flow. Mints a flow-state row, attaches it as the
 * admin flow cookie, and 302s to Google's authorize URL.
 *
 * Sign-up and sign-in resolve to the same entry point; the
 * `hd=labcraft.dev` enforcement lives in `GoogleWorkspaceProvider`.
 */
export const BeginRoute = defineRoute({
  method: 'GET',
  path: '/api/account/google-workspace/authorize',
  response: z.void(),
  description:
    'Begin a Google Workspace OAuth flow (admin scope). 302 → Google authorize URL.',
})

/**
 * `GET /api/account/google-workspace/callback?code=…&state=…` —
 * Google's redirect target. Verifies the flow cookie matches `state`,
 * runs the OIDC exchange + `hd` check, upserts the admin row, mints
 * the session, and 302s to `/`. Failure paths 302 to
 * `/auth/expired` so the SPA can render a "sign-in expired" view.
 */
export const AuthCallbackRoute = defineRoute({
  method: 'GET',
  path: '/api/account/google-workspace/callback',
  response: z.void(),
  description:
    "Consume Google's authorization code, mint the admin session, 302 to /.",
})

////////////////////////////////////////////////////////////////////////////////
// /api/account/me
////////////////////////////////////////////////////////////////////////////////

/**
 * Schema for the admin row surfaced to the admin SPA. Cached in the
 * Redis session payload at mint time so `/api/account/me` doesn't
 * round-trip Postgres on every request.
 *
 * @property id - UUID primary key from the `admins` table.
 * @property email - Lower-cased Workspace-verified email.
 * @property displayName - Display name (Google `name` claim).
 * @property avatarUrl - Provider-hosted picture URL or null.
 */
export const SessionSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
})

/** Resolved admin session payload. */
export type Session = z.infer<typeof SessionSchema>

/**
 * Response of `GET /api/account/me`. `admin` is `null` for anonymous
 * requests (no cookie, expired session, deleted row) so the SPA can
 * route to the sign-in CTA.
 */
export const MeResponseSchema = z.object({
  admin: SessionSchema.nullable(),
})

/** Parsed `MeResponse`. */
export type MeResponse = z.infer<typeof MeResponseSchema>

/**
 * `GET /api/account/me` — return the authenticated admin (or `null`
 * for anonymous). Slides the session TTL on every hit so an active
 * admin's session never lapses.
 */
export const MeRoute = defineRoute({
  method: 'GET',
  path: '/api/account/me',
  response: MeResponseSchema,
  description: 'Return the authenticated admin session, or null.',
})

////////////////////////////////////////////////////////////////////////////////
// /api/account/signout
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `POST /api/account/signout`. Always `{ ok: true }`
 * regardless of whether a session existed — the operation is
 * idempotent.
 */
export const SignoutResponseSchema = z.object({
  ok: z.literal(true),
})

/** Parsed `SignoutResponse`. */
export type SignoutResponse = z.infer<typeof SignoutResponseSchema>

/**
 * `POST /api/account/signout` — drop the Redis session row and clear
 * the admin cookie. Idempotent.
 */
export const SignoutRoute = defineRoute({
  method: 'POST',
  path: '/api/account/signout',
  response: SignoutResponseSchema,
  description: 'Clear the admin session cookie and Redis row.',
})

////////////////////////////////////////////////////////////////////////////////
// /api/learners — admin-scope list of learners (read-only)
////////////////////////////////////////////////////////////////////////////////

/**
 * Status of a learner in the Learners queue. Mirrors `learners.status`
 * on the account daemon's schema; the admin UI surfaces the term
 * verbatim.
 */
export const LearnerStatusSchema = z.enum(['active', 'inactive', 'banned'])

/** A learner's status as seen in the Learners panel. */
export type LearnerStatus = z.infer<typeof LearnerStatusSchema>

/**
 * Admin-facing view of a single learner. Direct subset of the
 * account-daemon `Learner` shape — the admin daemon proxies the call
 * through `AccountClient.listLearners` and re-emits the payload
 * without mutation.
 *
 * @property id - UUID primary key from the account daemon.
 * @property email - Lower-cased email address.
 * @property displayName - Free-form display name.
 * @property status - Access state.
 * @property createdAt - ISO-8601 timestamp the learner signed up.
 * @property lastLoginAt - ISO-8601 timestamp of the learner's most
 *   recent authentication. A LOGIN signal, not general activity —
 *   sessions persist, so it lags real last activity. The panel's default
 *   sort column (most-recent-login first).
 * @property profileCompletedAt - ISO-8601 timestamp the learner first
 *   filled in their profile; `null` until then.
 * @property avatarUrl - Provider-hosted avatar URL captured from the
 *   IdP claim. `null` for email-mode learners.
 * @property timezone - IANA zone captured at sign-up (e.g.
 *   `America/New_York`); `null` when unknown. Operator-facing signal
 *   for batch-activation cohort composition across zones.
 */
export const LearnerSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  status: LearnerStatusSchema,
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime(),
  profileCompletedAt: z.string().datetime().nullable(),
  avatarUrl: z.string().nullable(),
  timezone: z.string().nullable(),
})

/** Parsed `Learner`. */
export type Learner = z.infer<typeof LearnerSchema>

/**
 * Sortable columns for the Learners panel, mirroring the account
 * daemon's list surface (forwarded verbatim through the proxy):
 * `lastLogin` → last authentication, `createdAt` → signup,
 * `displayName` → name. List-only; never a recipient-filter concern.
 */
export const LearnerSortFieldSchema = z.enum([
  'lastLogin',
  'createdAt',
  'displayName',
])

/** Sortable list column. */
export type LearnerSortField = z.infer<typeof LearnerSortFieldSchema>

/** Sort direction. */
export const LearnerSortDirSchema = z.enum(['asc', 'desc'])

/** Sort direction. */
export type LearnerSortDir = z.infer<typeof LearnerSortDirSchema>

/**
 * Composable recipient-subset filters for `GET /api/learners`, forwarded
 * verbatim to the account daemon's `listLearners` (#1598 Part 7). Every
 * field optional; the set composes as AND. `status` + `q` are the
 * pre-existing Learners-panel filters; the rest were added so the Emails
 * compose surface can scope a broadcast by signup window, timezone,
 * acquisition, and progress signal without a second list route.
 *
 * @property status - Access-state filter. Omit for "all".
 * @property q - Case-insensitive substring match on email or display
 *   name.
 * @property signedUpAfter - Inclusive lower bound on signup time
 *   (ISO-8601 datetime).
 * @property signedUpBefore - Inclusive upper bound on signup time
 *   (ISO-8601 datetime).
 * @property timezone - Exact match on the learner's captured IANA zone.
 * @property acquisitionSource - Exact match on the granular acquisition
 *   source token.
 * @property acquisitionCategory - Exact match on the coarse acquisition
 *   category (`campaign` / `organic` / `referral` / `direct`).
 * @property hasProgress - `true` keeps learners who have started a
 *   course; `false` keeps those who never have; absent applies no
 *   progress filter.
 * @property tags - Tag INCLUDE filter (#1639): keep learners carrying any
 *   of the listed tags (union). Admin forwards these RAW — the account
 *   daemon is the sole canonicalizer, so this schema deliberately does
 *   not import or apply `LearnerTagSchema`. Empty/absent = no constraint.
 * @property tagsNot - Tag EXCLUDE filter (#1639): drop learners carrying
 *   any of the listed tags (none-of). Same raw-forward + empty semantics
 *   as `tags`.
 */
export const LearnerListFilterSchema = z.object({
  status: LearnerStatusSchema.optional(),
  q: z.string().trim().min(1).optional(),
  signedUpAfter: z.string().datetime().optional(),
  signedUpBefore: z.string().datetime().optional(),
  timezone: z.string().trim().min(1).optional(),
  acquisitionSource: z.string().trim().min(1).optional(),
  acquisitionCategory: CoreAnalytics.AcquisitionCategorySchema.optional(),
  hasProgress: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  tagsNot: z.array(z.string()).optional(),
})

/** Parsed recipient-subset filter set for the Learners list route. */
export type LearnerListFilters = z.infer<typeof LearnerListFilterSchema>

/**
 * Response of `GET /api/learners`.
 *
 * @property learners - Page of learners matching the requested filter.
 * @property total - Count of learners matching the filter across every
 *   page, so the panel can render a numbered pager (pages =
 *   ceil(total / limit)).
 * @property tagsByLearnerId - The tags carried by each learner in this
 *   page, keyed by learner id (#1639). Forwarded verbatim from the account
 *   daemon's list sidecar — admin holds no learner DB access, so this is
 *   what seeds the detail-drawer chips without a per-row round-trip. Every
 *   returned learner has an entry (an empty array when untagged).
 */
export const LearnersListResponseSchema = z.object({
  learners: z.array(LearnerSchema),
  total: z.number().int().min(0),
  tagsByLearnerId: z.record(z.string(), z.array(z.string())),
})

/** Parsed `LearnersListResponse`. */
export type LearnersListResponse = z.infer<typeof LearnersListResponseSchema>

/**
 * `GET /api/learners?status=&q=&limit=&offset=&sort=&dir=` — list
 * learners for the admin. Behind the admin session cookie; returns 401
 * for anonymous requests.
 */
export const LearnersListRoute = defineRoute({
  method: 'GET',
  path: '/api/learners',
  response: LearnersListResponseSchema,
  description: 'List learners for the Learners panel. Admin-only.',
})

////////////////////////////////////////////////////////////////////////////////
// /api/learners/:id/status — operator-only per-learner moderation
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /api/learners/:id/status`. Flips a learner between
 * `active` / `inactive` / `banned`. Idempotent; setting the current
 * status returns the row unchanged.
 *
 * @property status - Target access state.
 */
export const SetLearnerStatusRequestSchema = z.object({
  status: LearnerStatusSchema,
})

/** Parsed `SetLearnerStatusRequest`. */
export type SetLearnerStatusRequest = z.infer<
  typeof SetLearnerStatusRequestSchema
>

/**
 * Response of `POST /api/learners/:id/status`. Returns the updated
 * `Learner` row so the admin SPA can re-render the row without
 * a follow-up GET.
 */
export const SetLearnerStatusResponseSchema = z.object({
  learner: LearnerSchema,
})

/** Parsed `SetLearnerStatusResponse`. */
export type SetLearnerStatusResponse = z.infer<
  typeof SetLearnerStatusResponseSchema
>

/**
 * Params for `POST /api/learners/:id/status`. UUID-shaped so a
 * non-UUID id (typo, fuzzer) trips the backend's params-validation
 * gate and yields 400 before the handler ever runs.
 */
export const SetLearnerStatusParamsSchema = z.object({
  id: z.string().uuid(),
})

/**
 * `POST /api/learners/:id/status` — operator action to flip a
 * learner's access state. Behind the admin session cookie; returns
 * 401 for anonymous requests, 404 when the id doesn't match a
 * learner, 400 on an invalid status value or a malformed UUID.
 */
export const SetLearnerStatusRoute = defineRoute({
  method: 'POST',
  path: '/api/learners/:id/status',
  params: SetLearnerStatusParamsSchema,
  request: SetLearnerStatusRequestSchema,
  response: SetLearnerStatusResponseSchema,
  description: 'Set a learner’s access state. Admin-only.',
})

////////////////////////////////////////////////////////////////////////////////
// /api/learners tags (#1639) — admin-gated proxy over the account tag routes
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /api/learners/:id/tags`. The single tag to attach. Admin
 * forwards the raw string to the account daemon, which is the sole
 * canonicalizer (trim → lowercase → validate) — so this schema requires only
 * a non-empty string and deliberately does NOT mirror the account's
 * `LearnerTagSchema` (no comma / length rules here; the account daemon owns
 * those and returns a clean 400 on a malformed tag).
 *
 * @property tag - The tag to attach (canonicalized downstream).
 */
export const AttachTagRequestSchema = z.object({
  tag: z.string().min(1),
})

/** Parsed `AttachTagRequest`. */
export type AttachTagRequest = z.infer<typeof AttachTagRequestSchema>

/**
 * Response shared by every admin tag route: a flat, ordered tag list. For
 * the per-learner routes it is that learner's tag set after the operation;
 * for the list-all route it is every distinct tag across learners. Forwarded
 * verbatim from the account daemon's matching response.
 *
 * @property tags - The ordered (alphabetical) tag list.
 */
export const LearnerTagsResponseSchema = z.object({
  tags: z.array(z.string()),
})

/** Parsed `LearnerTagsResponse`. */
export type LearnerTagsResponse = z.infer<typeof LearnerTagsResponseSchema>

/**
 * `POST /api/learners/:id/tags` — attach a tag to a learner (admin-gated
 * proxy). Returns the learner's tag set after the attach. 401 for anonymous.
 */
export const AttachTagRoute = defineRoute({
  method: 'POST',
  path: '/api/learners/:id/tags',
  request: AttachTagRequestSchema,
  response: LearnerTagsResponseSchema,
  description: "Attach a tag to a learner; returns the learner's tag set.",
})

/**
 * `DELETE /api/learners/:id/tags/:tag` — detach a tag from a learner
 * (admin-gated proxy). Returns the learner's tag set after the detach. 401
 * for anonymous.
 */
export const DetachTagRoute = defineRoute({
  method: 'DELETE',
  path: '/api/learners/:id/tags/:tag',
  response: LearnerTagsResponseSchema,
  description: "Detach a tag from a learner; returns the learner's tag set.",
})

/**
 * `GET /api/learners/:id/tags` — list one learner's tags (admin-gated
 * proxy), alphabetical. The drawer's authoritative single-learner read. 401
 * for anonymous.
 */
export const ListLearnerTagsRoute = defineRoute({
  method: 'GET',
  path: '/api/learners/:id/tags',
  response: LearnerTagsResponseSchema,
  description: "List a learner's tags. Admin-only.",
})

/**
 * `GET /api/learners/tags` — list every distinct tag across all learners
 * (admin-gated proxy), alphabetical. Backs the tag autocomplete. A static
 * path with no `GET /api/learners/:id` sibling in the admin surface, so it
 * never collides. 401 for anonymous.
 */
export const ListAllTagsRoute = defineRoute({
  method: 'GET',
  path: '/api/learners/tags',
  response: LearnerTagsResponseSchema,
  description: 'List every distinct tag across all learners. Admin-only.',
})

////////////////////////////////////////////////////////////////////////////////
// /api/learners/:id/profile (PATCH #1639) — operator partial profile edit
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `PATCH /api/learners/:id/profile` — the operator-facing partial
 * profile edit (#1639). Every field is optional; only present fields are
 * written. `email` is deliberately absent (read-only by construction) and,
 * since `z.object` strips unknown keys, an `email` in the body is ignored.
 * `timezone` is forwarded raw — the account daemon is the authority and
 * validates it against `Intl` (a bad zone comes back as a 400 the handler
 * passes through), so admin only requires a non-empty string here.
 *
 * @property displayName - Operator-set display name (1..80, trimmed).
 * @property timezone - Operator-set IANA timezone (validated downstream).
 * @property avatarUrl - Operator-set avatar URL.
 */
export const UpdateLearnerProfileRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(80).optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  avatarUrl: z.string().url().optional(),
})

/** Parsed `UpdateLearnerProfileRequest`. */
export type UpdateLearnerProfileRequest = z.infer<
  typeof UpdateLearnerProfileRequestSchema
>

/**
 * Response of `PATCH /api/learners/:id/profile`. Returns the updated learner
 * (admin subset) so the drawer re-renders without a follow-up read.
 *
 * @property learner - The updated learner row.
 */
export const UpdateLearnerProfileResponseSchema = z.object({
  learner: LearnerSchema,
})

/** Parsed `UpdateLearnerProfileResponse`. */
export type UpdateLearnerProfileResponse = z.infer<
  typeof UpdateLearnerProfileResponseSchema
>

/**
 * `PATCH /api/learners/:id/profile` — operator partial edit of a learner's
 * `display_name` / `timezone` / `avatar_url` (admin-gated proxy over the
 * account daemon's PATCH). `email` is not writable. 401 for anonymous, 404
 * for an unknown id, 400 on an invalid field (forwarded from the account
 * daemon's validation).
 */
export const UpdateLearnerProfileRoute = defineRoute({
  method: 'PATCH',
  path: '/api/learners/:id/profile',
  request: UpdateLearnerProfileRequestSchema,
  response: UpdateLearnerProfileResponseSchema,
  description:
    'Operator partial edit of a learner display_name / timezone / avatar_url. Admin-only.',
})
